import { CompensationType, EnrollmentMode } from "./types";

export const COMPENSATION_OPTIONS: { label: string; value: CompensationType }[] = [
  { label: "Monthly", value: "MONTHLY" },
  { label: "Hourly", value: "HOURLY" },
  { label: "Per class", value: "PER_CLASS" },
  { label: "Custom (manual per session)", value: "CUSTOM_PER_CLASS" },
];

/** No fixed rate — each session's payout is entered by hand after it's done. */
export const isCustomPerClassCompensation = (type?: string) =>
  String(type || "").toUpperCase() === "CUSTOM_PER_CLASS";

/**
 * Whether a payout session's amount actually has a value — checked from the
 * amount itself rather than trusting `amount_entered` alone, since a session
 * with a real number should never read as pending regardless of whether that
 * flag came through correctly.
 */
export const isPayoutAmountSet = (session?: {
  amount?: number | string | null;
  amount_entered?: boolean;
}) => {
  if (!session) return false;
  if (session.amount_entered === true) return true;
  if (session.amount === null || session.amount === undefined) return false;
  return Number.isFinite(Number(session.amount));
};

export const ENROLLMENT_MODE_OPTIONS: {
  label: string;
  value: EnrollmentMode;
}[] = [
  { label: "Both (membership + drop-in)", value: "BOTH" },
  { label: "Fixed membership", value: "FIXED_ROSTER" },
  { label: "Drop-in only", value: "DROP_IN" },
];

export const WEEK_DAY_OPTIONS = [
  { label: "Sunday", value: "0" },
  { label: "Monday", value: "1" },
  { label: "Tuesday", value: "2" },
  { label: "Wednesday", value: "3" },
  { label: "Thursday", value: "4" },
  { label: "Friday", value: "5" },
  { label: "Saturday", value: "6" },
];

export const RECURRENCE_FREQUENCY_OPTIONS = [
  { label: "Daily", value: "DAILY" },
  { label: "Weekly", value: "WEEKLY" },
  { label: "Monthly", value: "MONTHLY" },
];

export const ACTIVE_OPTIONS = [
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
];

export const toActiveLabel = (active: any) =>
  active === false || active === "Inactive" ? "Inactive" : "Active";

export const fromActiveLabel = (value: any) => String(value) === "Active";

/**
 * A coach is inactive if either side says so: create only sets the staff
 * account's flag, while update writes both it and the wage profile's.
 */
export const isCoachActive = (coach: any) =>
  coach?.coach_profile?.active !== false && coach?.active !== false;

export const labelForValue = (
  options: { label: string; value: string }[],
  value: any
) => options.find((o) => o.value === String(value))?.label || "-";

/**
 * Builds a local Date from a `YYYY-MM-DD` date and an `HH:mm` time. Local (not
 * UTC) so `toISOString()` sends the moment the user actually picked, matching
 * how the appointment form builds its payload.
 */
export const combineDateAndTime = (
  dateStr?: string | null,
  timeStr?: string | null
): Date | null => {
  if (!dateStr) return null;
  const datePart = String(dateStr).trim().slice(0, 10);
  const match = String(timeStr || "00:00")
    .trim()
    .match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const parsed = new Date(`${datePart}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;

  parsed.setHours(hours, minutes, 0, 0);
  return parsed;
};

export const addMinutes = (date: Date, minutes: number): Date =>
  new Date(date.getTime() + minutes * 60000);

export const formatTime = (value: any): string => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  const hours24 = d.getHours();
  const hours12 = hours24 % 12 || 12;
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${hours12}:${minutes} ${hours24 >= 12 ? "PM" : "AM"}`;
};

export const formatMoney = (value: any): string => {
  if (value === undefined || value === null || value === "") return "-";
  const num = Number(value);
  return Number.isFinite(num) ? `${num.toFixed(2)} $` : String(value);
};

export const SESSION_STATUS_COLORS: Record<string, string> = {
  PENDING: "#ed6c02",
  CONFIRMED: "#1976d2",
  DONE: "#2e7d32",
  CANCELLED: "#d32f2f",
};

/** Turns a raw ALL_CAPS status enum (session, attendance, package, source…) into plain Title Case for display. */
export const formatStatusLabel = (status?: string | null): string => {
  const value = String(status || "").trim();
  if (!value) return "-";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const isSessionCancelled = (status?: string) =>
  String(status || "").toUpperCase() === "CANCELLED";

/**
 * A roster entry stays in the list forever once cancelled (the row isn't
 * deleted, just flipped to CANCELLED) so it can be re-booked or kept for
 * history — only BOOKED entries count toward capacity or block a client from
 * being picked again.
 */
export const isAttendanceBooked = (status?: string) =>
  String(status || "").toUpperCase() === "BOOKED";

export const isSessionPast = (endAt?: string) => {
  if (!endAt) return false;
  const d = new Date(endAt);
  return !Number.isNaN(d.getTime()) && d.getTime() < Date.now();
};

/**
 * Whether a session's scheduled start has already passed — the boundary for
 * whether it's still bookable. Used instead of `isSessionPast` (end-time
 * based, used for dimming rows in lists) because you can't book someone into
 * a class that's already started, even if it hasn't ended yet.
 */
export const isSessionStartInPast = (startAt?: string) => {
  if (!startAt) return false;
  const d = new Date(startAt);
  return !Number.isNaN(d.getTime()) && d.getTime() <= Date.now();
};

/* ----------------------------- open-ended classes ---------------------------- */

/**
 * A class with a recurrence rule but no end date and no session count runs
 * "until further notice" — the backend never writes a last session for it, it
 * just keeps projecting new dates.
 */
export const isOpenEndedClass = (classItem?: {
  recurrence_frequency?: any;
  recurrence_end?: any;
  recurrence_count?: any;
}) =>
  Boolean(classItem?.recurrence_frequency) &&
  !classItem?.recurrence_end &&
  !classItem?.recurrence_count;

export const isRecurringClass = (classItem?: { recurrence_frequency?: any }) =>
  Boolean(classItem?.recurrence_frequency);

/** `yyyy-mm-dd` in the user's own timezone (not UTC — a date, not a moment). */
export const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export interface SessionRange {
  start: string;
  end: string;
}

/** The window `GET /classes/:id/sessions` defaults to when given no bounds. */
export const currentMonthRange = (anchor: Date = new Date()): SessionRange => {
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  return { start: toDateInputValue(start), end: toDateInputValue(end) };
};

/**
 * Steps the window a whole month at a time, anchored on its start. Used by the
 * session list's prev/next arrows — an open-ended class has no last page to
 * stop at, so navigation is unbounded on purpose.
 */
export const shiftRangeByMonths = (
  range: SessionRange,
  months: number
): SessionRange => {
  const start = new Date(`${range.start}T00:00:00`);
  if (Number.isNaN(start.getTime())) return currentMonthRange();
  return currentMonthRange(
    new Date(start.getFullYear(), start.getMonth() + months, 1)
  );
};

export const formatRangeLabel = (range?: SessionRange | null): string => {
  if (!range?.start) return "";
  const start = new Date(`${String(range.start).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(start.getTime())) return String(range.start);
  return start.toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

/**
 * A projected session has no row behind it, so nothing about it is stable
 * except the class id and the date — which is exactly what its `v:` id is
 * made of, making the id itself the only safe key.
 */
export const sessionRowKey = (
  session: { id?: string | null; start_at?: string },
  index: number
): string => String(session?.id || session?.start_at || index);

/**
 * A roster entry's `id` is null on a projected session, and several projected
 * entries share that null — so the client is what identifies the row.
 */
export const rosterRowKey = (
  entry: { id?: string | null; client_id?: string },
  index: number
): string => String(entry?.id || entry?.client_id || index);

/**
 * Calendar-date comparison, deliberately not an instant one: creating a 09:00
 * class at 09:30 is an ordinary thing to do, and should not be rejected as
 * "in the past".
 */
export const isDateBeforeToday = (date: Date): boolean => {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return date.getTime() < startOfToday.getTime();
};

/** Days from now, rounded down — used against the 366-day booking horizon. */
export const daysFromNow = (value?: string | null): number | null => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((d.getTime() - Date.now()) / 86400000);
};
